import { appendStreamingContent } from './streamingContent';
import type { StreamUiHandlers } from './streamUiReducer';

type ChatActionType =
  | 'chat_start'
  | 'chat_resume_stream'
  | 'chat_append'
  | 'chat_complete'
  | 'chat_set_questions'
  | 'chat_stop'
  | 'chat_interrupt'
  | 'chat_rate_limit'
  | 'chat_model_fallback'
  | 'chat_clear_model_fallback'
  | 'chat_clear_questions'
  | 'chat_set_approaches'
  | 'chat_clear_approaches'
  | 'review_loading'
  | 'review_result'
  | 'chat_reconnect'
  | 'phase_changed';

/** Handlers do chat da fase, da revisão e da troca de fase. */
export const chatHandlers: Pick<StreamUiHandlers, ChatActionType> = {
  chat_start: state => ({
    ...state,
    isStreaming: true,
    isThinking: true,
    streamingContent: '',
    pendingQuestions: undefined,
    pendingApproaches: undefined,
    rateLimitInfo: undefined,
    modelFallbackNotice: undefined,
    phaseCompleted: false,
    everStartedStreaming: true,
  }),
  chat_resume_stream: state => ({
    ...state,
    isStreaming: true,
    phaseCompleted: false,
    everStartedStreaming: true,
    rateLimitInfo: undefined,
  }),
  chat_append: (state, action) => ({
    ...state,
    isThinking: false,
    streamingContent: appendStreamingContent(
      state.streamingContent,
      action.content,
    ),
  }),
  chat_complete: state => ({
    ...state,
    isStreaming: false,
    isThinking: false,
    pendingQuestions: undefined,
    pendingApproaches: undefined,
    phaseCompleted: true,
  }),
  chat_set_questions: (state, action) => ({
    ...state,
    pendingQuestions: action.questions,
    streamingContent: appendStreamingContent(
      state.streamingContent,
      action.content,
    ),
  }),
  chat_stop: state => ({
    ...state,
    isStreaming: false,
    isThinking: false,
    // Preserve streamingContent when questions or approaches are pending
    // (SSE may drop while waiting for user answer/selection).
    // Cleared by chat_clear_questions / chat_clear_approaches.
    streamingContent:
      state.pendingQuestions || state.pendingApproaches
        ? state.streamingContent
        : '',
  }),
  chat_interrupt: state => ({
    ...state,
    isStreaming: false,
    isThinking: false,
    streamingContent: '',
    pendingQuestions: undefined,
    pendingApproaches: undefined,
  }),
  chat_rate_limit: (state, action) => ({
    ...state,
    isStreaming: false,
    rateLimitInfo: {
      message: action.content,
      resetTime: action.resetTime,
      timezone: action.timezone,
    },
  }),
  chat_model_fallback: (state, action) => ({
    ...state,
    modelFallbackNotice: action.content,
  }),
  chat_clear_model_fallback: state => ({
    ...state,
    modelFallbackNotice: undefined,
  }),
  // Keep streamingContent visible:
  // - While streaming is active, the content hasn't been persisted yet.
  // - While streaming is NOT active (SSE dropped), keep the partial
  //   AI response visible until message polling replaces it with the
  //   full persisted response from the backend.
  chat_clear_questions: state => ({
    ...state,
    pendingQuestions: undefined,
    streamingContent: state.streamingContent,
  }),
  chat_set_approaches: (state, action) => ({
    ...state,
    pendingApproaches: action.approaches,
    streamingContent: action.content
      ? appendStreamingContent(state.streamingContent, action.content)
      : state.streamingContent,
  }),
  chat_clear_approaches: state => ({
    ...state,
    pendingApproaches: undefined,
  }),
  review_loading: state => ({
    ...state,
    isReviewLoading: true,
    reviewResult: undefined,
  }),
  review_result: (state, action) => ({
    ...state,
    isReviewLoading: false,
    reviewResult: action.result,
  }),
  // Reset streaming flags after an SSE disconnection without clearing
  // tasks/executionState (which belong to the ongoing phase execution).
  // Keep partial content visible until backend sync confirms the terminal state.
  chat_reconnect: state => ({
    ...state,
    isStreaming: false,
    phaseCompleted: false,
    everStartedStreaming: false,
    streamingContent: state.streamingContent,
  }),
  // Reset ALL streaming/completion state when transitioning to a different phase.
  // Without this, flags from a previous phase (e.g. QA's isStreaming=true from
  // qa_progress) leak into the new phase (e.g. Pronto), causing the chat to be
  // stuck in "Aguardando resposta..." and blocking the auto-start flow.
  phase_changed: state => ({
    ...state,
    isStreaming: false,
    phaseCompleted: false,
    everStartedStreaming: false,
    streamingContent: '',
    pendingQuestions: undefined,
    pendingApproaches: undefined,
  }),
};
